import { action, computed, observable } from "mobx";
import TourJobStore from "~/stores/TourJob/TourJobStore";
import TourJobStatus from "~/constants/TourJob/TourJobStatus";
import ProductStore from "~/stores/ProductStore";
import Immersive3dTourStore from "~/stores/Immersive3dTour/Immersive3dTourStore";
import Immersive3dTourTabStore from "~/stores/Immersive3dTour/Immersive3dTourTabStore";
import Immersive3dTourFloorPlanImportModalStore from "~/applications/immersive_3d_tour/stores/Immersive3dTourFloorPlanImportModalStore";
import Immersive3dTourTabReplacePanoramaStore from "~/stores/Immersive3dTour/Immersive3dTourTabReplacePanoramaStore";
import Immersive3dTourPlayerStore from "~/stores/Immersive3dTour/Immersive3dTourPlayerStore";
import Immersive3dTourSource from "~/constants/Immersive3dTour/Immersive3dTourSource";

class Immersive3dTourAppPageStore {
  /**
   * @type {number|null}
   */
  tourId = null;

  /**
   * @property {Immersive3dTourStore}
   */
  immersive3dTourStore;

  /**
   * @property {Immersive3dTourTabStore}
   */
  immersive3dTourTabStore;

  /**
   * @property {Immersive3dTourTabReplacePanoramaStore}
   */
  immersive3dTourTabReplacePanoramaStore;

  /**
   * @property {TourJobStore}
   */
  tourJobStore;

  /**
   * @property {ProductStore}
   */
  productStore;

  /**
   * @property {Immersive3dTourFloorPlanImportModalStore}
   */
  immersive3dTourFloorPlanImportModalStore;

  /**
   * @property {Immersive3dTourPlayerStore}
   */
  immersive3dTourPlayerStore;

  timerForProcessingRefresh = null;

  @observable isReplacePanoramaPage = false;

  constructor(data = {}) {
    if (typeof data.tourId !== "undefined") {
      this.setTourId(data.tourId);
    }
  }

  @action
  setTourId(tourId) {
    this.tourId = tourId;

    this.immersive3dTourStore = new Immersive3dTourStore();
    this.immersive3dTourStore.tourId = tourId;

    this.immersive3dTourTabStore = new Immersive3dTourTabStore(this.immersive3dTourStore);
    this.immersive3dTourTabStore.setRootStore(this);

    this.immersive3dTourTabReplacePanoramaStore = new Immersive3dTourTabReplacePanoramaStore(this.immersive3dTourStore);
    this.immersive3dTourTabReplacePanoramaStore.setRootStore(this);

    this.immersive3dTourPlayerStore = new Immersive3dTourPlayerStore(this.immersive3dTourStore);
    this.immersive3dTourPlayerStore.allowEdition = false;

    this.tourJobStore = new TourJobStore(this);

    this.productStore = new ProductStore();

    this.immersive3dTourFloorPlanImportModalStore = new Immersive3dTourFloorPlanImportModalStore();
  }

  @computed
  get isLoading() {
    return (
      this.immersive3dTourStore.isLoading ||
      this.tourJobStore.isLoading ||
      this.productStore.isLoading ||
      this.immersive3dTourTabReplacePanoramaStore.isLoading
    );
  }

  @action
  load(withLoading = true) {
    if (withLoading) {
      this.immersive3dTourStore.isLoading = true;
      this.tourJobStore.isLoading = true;
    }

    return Promise.all([this.immersive3dTourStore.getOneOrCreate(), this.tourJobStore.getAll()]).finally(() => {
      this.processingAutoRefresh();
      this.immersive3dTourStore.isLoading = false;
      this.tourJobStore.isLoading = false;
    });
  }

  @computed
  get hasCompletedInitialProcessing() {
    const { immersive3dTour } = this.immersive3dTourStore;
    const { hasNewPanoramasData } = immersive3dTour;

    // Initial processing
    // a) Immersive 3D Tour not in creation
    // b) No new panoramas data in the queue to process
    // c) Immersive 3D Tour Processed
    // d) Immersive 3D Tour is Manual OR it is UI Capture and the panoramas have been enhanced

    return (
      !immersive3dTour.isCreation &&
      !hasNewPanoramasData &&
      immersive3dTour.isProcessed &&
      (immersive3dTour.source === Immersive3dTourSource.MANUAL ||
        (immersive3dTour.source === Immersive3dTourSource.UI_CAPTURE &&
          immersive3dTour.panoramasEnhancedDts !== null) ||
        immersive3dTour.source === Immersive3dTourSource.UI_CAPTURE_CUSTOM)
    );
  }

  @computed
  get hasCompletedProcessingWithSuccess() {
    const { immersive3dTour } = this.immersive3dTourStore;
    const { immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob } = this.tourJobStore;

    // All the steps completed
    // a) Has completed initial processing
    // b) Created an Immersive 3D Tour Panoramas with or without floor plan TourJob
    // c) Immersive 3D Tour Panoramas with or without floor plan Tour Job status done
    // d) Has no active Replace panorama request

    return (
      this.hasCompletedInitialProcessing &&
      immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob &&
      immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob.status === TourJobStatus.DONE &&
      !immersive3dTour.hasActiveReplacePanoramaRequest
    );
  }

  @computed
  get isProcessing() {
    const { immersive3dTour } = this.immersive3dTourStore;
    const { hasNewPanoramasData } = immersive3dTour;
    const { immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob } = this.tourJobStore;

    // Still in process
    // a) Is processing replace panoramas
    // b) New panoramas data in the queue to process
    // c) Immersive 3D Tour not in creation
    // e) No Images alignment Tour Job OR if there is one, it's not done yet
    // f) Immersive 3D Tour is UI Capture and the panoramas have not been enhanced yet

    return (
      immersive3dTour.hasActiveReplacePanoramaRequest ||
      hasNewPanoramasData ||
      (!immersive3dTour.isCreation &&
        (!immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob ||
          (immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob &&
            immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob.status !== TourJobStatus.DONE))) ||
      (!immersive3dTour.isCreation &&
        immersive3dTour.source === Immersive3dTourSource.UI_CAPTURE &&
        immersive3dTour.panoramasEnhancedDts === null)
    );
  }

  processingAutoRefresh() {
    const { immersive3dTour: immersiveTourOriginal } = this.immersive3dTourStore;

    clearInterval(this.timerForProcessingRefresh);

    if (
      !immersiveTourOriginal.isCreation &&
      !this.hasCompletedProcessingWithSuccess &&
      !immersiveTourOriginal.isDenied
    ) {
      this.timerForProcessingRefresh = setInterval(() => {
        this.load(false).then(() => {
          const { immersive3dTour } = this.immersive3dTourStore;

          if (this.hasCompletedProcessingWithSuccess || immersive3dTour.isDenied) {
            clearInterval(this.timerForProcessingRefresh);
          }
        });
      }, 30000);
    }
  }

  @action
  goToReplacePanoramaPage() {
    this.immersive3dTourTabReplacePanoramaStore.load();
    this.setIsReplacePanoramaPage(true);
  }

  @action
  setIsReplacePanoramaPage(isReplacePanoramaPage) {
    this.isReplacePanoramaPage = isReplacePanoramaPage;
  }
}

export default Immersive3dTourAppPageStore;
